import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useCallback,
} from 'react'
import classNames from 'classnames'
import _ from 'lodash'

import { PopoverContextProvider } from '@/context'
import { ConditionalWrapper } from '../Wrapper'

import { IPopoverProps, ITriggerAction } from './types'
import Trigger from './trigger'
import Content from './content'

export const Popover = forwardRef<ITriggerAction, IPopoverProps>(
  (
    {
      className,
      disabled,
      initialOpen,
      button,
      NestedWrapEl,
      nestedElementClass,
      autoScroll = false,
      closeOnOutside = true,
      trigger = 'on-click',
      WrapEl = 'div',
      onOpened,
      onClosed,
      children,
    },
    ref,
  ) => {
    const elementRef = useRef<HTMLElement>(null)
    const [open, setOpen] = useState<boolean | undefined>()
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
      if (initialOpen !== undefined) {
        setOpen(initialOpen)
      }
    }, [initialOpen])

    useEffect(() => {
      if (open) {
        setTimeout(() => {
          document.body.classList.add('menu_opened')
        }, 100)
      } else {
        document.body.classList.remove('menu_opened')
      }
      return () => {
        document.body.classList.remove('menu_opened')
      }
    }, [open])

    const onScroll = useCallback(
      (block: 'start' | 'end') => {
        if (!autoScroll) return
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          elementRef.current?.scrollIntoView({
            block,
            behavior: 'smooth',
          })
        }, 200)
      },
      [autoScroll],
    )

    useEffect(() => {
      if (open === undefined) return

      if (open) {
        onOpened?.()
        onScroll('start')
      } else {
        onClosed?.()
        onScroll('end')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, onScroll])

    const showPopover = useCallback(() => {
      if (open) return
      _.delay(setOpen, 50, true)
    }, [open])

    const hidePopover = useCallback(() => {
      _.delay(setOpen, 200, false)
    }, [])

    return (
      <PopoverContextProvider
        onClose={hidePopover}
        closeOnOutside={closeOnOutside}
      >
        <ConditionalWrapper
          ref={elementRef}
          className={classNames('menu_item', className, { opened: !!open })}
          WrapEl={WrapEl}
          {...(WrapEl
            ? {
                onMouseLeave: () =>
                  trigger === 'mouse-over' ? hidePopover() : null,
              }
            : {})}
        >
          <ConditionalWrapper
            WrapEl={NestedWrapEl}
            className={classNames(nestedElementClass)}
          >
            <Trigger
              ref={ref}
              trigger={trigger}
              disabled={!!disabled}
              onClose={hidePopover}
              onTrigger={showPopover}
            >
              {button}
            </Trigger>
            {!!children && (
              <Content currentState={!!open}>
                {open && (
                  <>
                    {typeof children === 'function'
                      ? children(hidePopover)
                      : children}
                  </>
                )}
              </Content>
            )}
          </ConditionalWrapper>
        </ConditionalWrapper>
      </PopoverContextProvider>
    )
  },
)

export type IPopover = React.ElementRef<typeof Popover> | null

Popover.displayName = 'Popover'
